import React from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Trash2 } from 'lucide-react';
import * as vcDocApi from '../services/vcDocApi';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import CustomersNoticeFmtBar from './CustomersNoticeFmtBar';
import './CustomersManualClienteView.css';

function Tip({ label, placement = 'bottom', children }) {
  if (!label) return children;
  return (
    <span className={'cmc-manual-tip-wrap cmc-manual-tip-wrap--' + placement}>
      {children}
      <span className={'cmc-manual-tip cmc-manual-tip--' + placement} role="tooltip">
        {label}
      </span>
    </span>
  );
}

/** Reordena ids: move fromId para antes/depois de targetId. */
function reorderIds(items, fromId, targetId, before) {
  const ids = items.map((x) => x.id);
  const from = ids.indexOf(fromId);
  if (from < 0) return null;
  ids.splice(from, 1);
  let to = ids.indexOf(targetId);
  if (to < 0) return null;
  if (!before) to += 1;
  ids.splice(to, 0, fromId);
  if (ids.length === items.length && ids.every((id, i) => id === items[i].id)) return null;
  return ids;
}

function dropBeforeFromEvent(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  return e.clientY < rect.top + rect.height / 2;
}

function strip(h) {
  return String(h || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 110);
}

function escapeHtmlText(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Cola limpa: remove fonte/tamanho/cor/classes do Word/web e mantém só
 * estrutura do editor (h1/h2, listas, negrito/itálico, etc.).
 */
function normalizePasteHtml(html) {
  const root = document.createElement('div');
  root.innerHTML = String(html || '');

  root.querySelectorAll('script,style,meta,link,iframe,object,embed,form,input,button,svg,noscript').forEach((n) => n.remove());

  const BLOCK = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'pre', 'hr', 'br']);
  const INLINE = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'code', 'br']);
  const HEADING = { h3: 'h2', h4: 'h2', h5: 'h2', h6: 'h2' };

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent || '');
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    let tag = String(node.tagName || '').toLowerCase();
    if (HEADING[tag]) tag = HEADING[tag];

    // Imagens externas: não colar (só as do nosso fluxo com data-img)
    if (tag === 'img') {
      const guid = node.getAttribute('data-img');
      if (guid) {
        const img = document.createElement('img');
        img.setAttribute('data-img', String(guid).replace(/[^a-zA-Z0-9-]/g, '').slice(0, 36));
        const w = (node.getAttribute('style') || '').match(/width\s*:\s*([0-9.]+%)/i);
        img.setAttribute('style', w ? `width: ${w[1]};` : 'width: 100%;');
        return img;
      }
      return null;
    }

    if (tag === 'a') {
      const frag = document.createDocumentFragment();
      Array.from(node.childNodes).forEach((c) => {
        const n = walk(c);
        if (n) frag.appendChild(n);
      });
      return frag;
    }

    if (tag === 'span' || tag === 'font' || tag === 'label' || tag === 'o:p') {
      const style = String(node.getAttribute?.('style') || '').toLowerCase();
      const fw = ((style.match(/font-weight\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const fs = ((style.match(/font-style\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const td = ((style.match(/text-decoration(?:-line)?\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const wraps = [];
      if (/bold|[6-9]00/.test(fw)) wraps.push('b');
      if (/italic/.test(fs)) wraps.push('i');
      if (/underline/.test(td)) wraps.push('u');
      if (/line-through/.test(td)) wraps.push('s');

      let inner = document.createDocumentFragment();
      Array.from(node.childNodes).forEach((c) => {
        const n = walk(c);
        if (n) inner.appendChild(n);
      });
      if (!wraps.length) return inner;
      let cur = inner;
      wraps.reverse().forEach((t) => {
        const el = document.createElement(t);
        el.appendChild(cur);
        cur = el;
      });
      return cur;
    }

    if (BLOCK.has(tag) || INLINE.has(tag)) {
      const outTag = tag === 'strong' ? 'b' : tag === 'em' ? 'i' : tag === 'strike' ? 's' : tag;
      if (outTag === 'pre' || outTag === 'code') {
        const el = document.createElement(outTag);
        el.textContent = node.textContent || '';
        return el;
      }
      const el = document.createElement(outTag === 'div' ? 'div' : outTag);
      Array.from(node.childNodes).forEach((c) => {
        const n = walk(c);
        if (n) el.appendChild(n);
      });
      return el;
    }

    // Tags desconhecidas: só o texto interno
    const frag = document.createDocumentFragment();
    Array.from(node.childNodes).forEach((c) => {
      const n = walk(c);
      if (n) frag.appendChild(n);
    });
    return frag;
  };

  const out = document.createElement('div');
  Array.from(root.childNodes).forEach((c) => {
    const n = walk(c);
    if (n) out.appendChild(n);
  });

  // Texto solto na raiz → bloco
  Array.from(out.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && String(node.textContent || '').trim()) {
      const div = document.createElement('div');
      div.textContent = node.textContent;
      node.replaceWith(div);
    }
  });

  let htmlOut = out.innerHTML;
  // limpa leftovers de estilo/atributos
  htmlOut = htmlOut
    .replace(/\s(style|class|id|dir|lang|face|size|color|bgcolor|align|width|height)\s*=\s*(["'])[\s\S]*?\2/gi, '')
    .replace(/\s(style|class|id|dir|lang|face|size|color|bgcolor|align|width|height)\s*=\s*[^\s>]+/gi, '');
  return htmlOut;
}

function normalizePastePlainText(text) {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n');
  if (lines.length === 1) return escapeHtmlText(lines[0]);
  return lines.map((line) => `<div>${escapeHtmlText(line) || '<br>'}</div>`).join('');
}

export default class CustomersManualClienteView extends React.Component {
  state = {
    modules: [],
    metricas: { modulos: 0, topicos: 0, documentacoes: 0, topicosVazios: 0 },
    recentes: [],
    loading: true,
    loadError: null,
    open: {},
    sel: { view: 'overview' },
    menu: null,
    move: null,
    palette: false,
    query: '',
    imgBox: null,
    fmt: null,
    slash: null,
    saved: 'Salvo agora',
    name: null,
    del: null,
    backup: null,
    dropHint: null,
  };

  editorRef = React.createRef();
  searchRef = React.createRef();
  nameRef = React.createRef();
  loaded = null;
  drag = null;
  img = null;
  saveTimer = null;
  abortLoad = null;

  componentDidMount() {
    this.key = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        this.setState({ palette: true, query: '' });
      }
      if (e.key === 'Escape') {
        this.setState({
          palette: false,
          move: null,
          menu: null,
          slash: null,
          fmt: null,
          imgBox: null,
          name: null,
          del: null,
          backup: null,
        });
      }
    };
    document.addEventListener('keydown', this.key);
    this._onSelectionChange = () => {
      if (this._fmtSelRaf) cancelAnimationFrame(this._fmtSelRaf);
      this._fmtSelRaf = requestAnimationFrame(() => {
        this._fmtSelRaf = null;
        this.syncFmtFromSelection();
      });
    };
    document.addEventListener('selectionchange', this._onSelectionChange);
    this.reloadHierarchy();
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.key);
    document.removeEventListener('selectionchange', this._onSelectionChange);
    if (this._fmtSelRaf) cancelAnimationFrame(this._fmtSelRaf);
    if (this._fmtSelTimer) clearTimeout(this._fmtSelTimer);
    this.unbindImgTrack();
    this.unbindSlashOutside();
    this.flushEditorSave();
    if (this.abortLoad) this.abortLoad.abort();
  }

  bindSlashOutside = () => {
    if (this._slashOutsideBound) return;
    this._slashOutsideBound = true;
    this._onSlashOutside = (event) => {
      const t = event.target;
      if (t instanceof Element && t.closest('.cmc-manual-slash')) return;
      if (this.state.slash) this.setState({ slash: null });
    };
    document.addEventListener('mousedown', this._onSlashOutside, true);
  };

  unbindSlashOutside = () => {
    if (!this._slashOutsideBound) return;
    this._slashOutsideBound = false;
    if (this._onSlashOutside) {
      document.removeEventListener('mousedown', this._onSlashOutside, true);
      this._onSlashOutside = null;
    }
  };

  /** Persiste o editor na doc atual (memória + API). Cancela autosave pendente. */
  flushEditorSave = async ({ api = true, quiet = false } = {}) => {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    const sel = this.state.sel;
    if (sel.view !== 'doc') return;
    const el = this.editorRef.current;
    const { d } = this.find(sel);
    if (!el || !d) return;
    this.normalizeEditorInlineFormats();
    const html = el.innerHTML;
    d.html = html;
    this.syncRemovedImages(d.id, el);
    if (!api) return;
    try {
      await vcDocApi.saveDocumentationContent(d.id, html);
      if (!quiet) this.setState({ saved: 'Salvo agora' });
    } catch (_) {
      if (!quiet) this.setState({ saved: 'Erro ao salvar' });
    }
  };

  scheduleEditorSave = (docId) => {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(async () => {
      this.saveTimer = null;
      const sel = this.state.sel;
      if (sel.view !== 'doc' || sel.docId !== docId) return;
      const el = this.editorRef.current;
      const d = this.doc();
      if (!el || !d || d.id !== docId) return;
      this.normalizeEditorInlineFormats();
      const html = el.innerHTML;
      d.html = html;
      try {
        await vcDocApi.saveDocumentationContent(docId, html);
        this.setState({ saved: 'Salvo agora' });
      } catch (_) {
        this.setState({ saved: 'Erro ao salvar' });
      }
    }, 800);
  };

  /** Troca seleção; faz flush antes de sair de uma documentação aberta. */
  changeSel = async (sel, patch = {}) => {
    const prev = this.state.sel;
    const leavingDoc =
      prev.view === 'doc' &&
      (sel.view !== 'doc' ||
        sel.docId !== prev.docId ||
        sel.moduleId !== prev.moduleId ||
        sel.topicId !== prev.topicId);
    if (leavingDoc) {
      await this.flushEditorSave({ quiet: true });
      this.loaded = null;
      this.clearImgSelection();
    }
    this.setState({ sel, ...patch });
  };

  reloadHierarchy = async (opts = {}) => {
    if (this.abortLoad) this.abortLoad.abort();
    await this.flushEditorSave({ quiet: true });
    const ac = new AbortController();
    this.abortLoad = ac;
    if (!opts.silent) this.setState({ loading: true, loadError: null });
    try {
      const data = await vcDocApi.fetchHierarchy(ac.signal);
      const modules = vcDocApi.mapHierarchyToModules(data);
      this.setState((s) => {
        // Preserva HTML já carregado no editor (hierarquia não traz body)
        const htmlById = new Map();
        for (const m of s.modules || []) {
          for (const t of m.topics || []) {
            for (const d of t.docs || []) {
              if (d.html) htmlById.set(d.id, d.html);
            }
          }
        }
        for (const m of modules) {
          for (const t of m.topics) {
            for (const d of t.docs) {
              if (htmlById.has(d.id)) d.html = htmlById.get(d.id);
            }
          }
        }
        return {
          modules,
          metricas: data.metricas || s.metricas,
          recentes: data.recentes || [],
          loading: false,
          loadError: null,
          open: s.open || {},
        };
      });
    } catch (e) {
      if (e.name === 'AbortError') return;
      this.setState({ loading: false, loadError: e.message || 'Falha ao carregar' });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    this.syncEditor();
    if (this.state.palette && this.searchRef.current) this.searchRef.current.focus();
    if (this.state.name && this.nameRef.current && document.activeElement !== this.nameRef.current) {
      this.nameRef.current.focus();
      this.nameRef.current.select();
    }
    if (this.state.slash && !prevState.slash) {
      requestAnimationFrame(() => {
        if (this.state.slash) this.bindSlashOutside();
      });
    } else if (!this.state.slash && prevState.slash) {
      this.unbindSlashOutside();
    }
  }

  syncEditor() {
    const d = this.doc();
    const el = this.editorRef.current;
    if (!el) {
      this.loaded = null;
      return;
    }
    if (this.loaded !== (d && d.id)) {
      el.innerHTML = (d && d.html) || '';
      this.loaded = d && d.id;
    }
  }

  mods() {
    return this.state.modules;
  }

  find(ids) {
    const m = this.mods().find((x) => x.id === ids.moduleId);
    const t = m && ids.topicId ? m.topics.find((x) => x.id === ids.topicId) : null;
    const d = t && ids.docId ? t.docs.find((x) => x.id === ids.docId) : null;
    return { m, t, d };
  }

  doc() {
    const s = this.state.sel;
    return s.view === 'doc' ? this.find(s).d : null;
  }

  update(fn) {
    const next = JSON.parse(JSON.stringify(this.state.modules));
    fn(next);
    this.setState({ modules: next, menu: null, move: null });
  }

  loc(docId) {
    for (const m of this.mods()) {
      for (const t of m.topics) {
        for (const d of t.docs) {
          if (d.id === docId) return { m, t, d };
        }
      }
    }
    return null;
  }

  ask(cfg) {
    this.setState({ name: cfg, menu: null, imgBox: null, fmt: null, slash: null });
  }

  newModule = () =>
    this.ask({
      mode: 'newModule',
      value: '',
      title: 'Novo módulo',
      sub: '1º nível da hierarquia',
      ph: 'Ex. Financeiro',
      cta: 'Criar módulo',
    });

  addTopic(moduleId) {
    const m = this.mods().find((x) => x.id === moduleId);
    this.ask({
      mode: 'newTopic',
      moduleId,
      value: '',
      title: 'Novo tópico',
      sub: 'Dentro de ' + m.name,
      ph: 'Ex. Contas a pagar',
      cta: 'Criar tópico',
    });
  }

  commitName = async () => {
    const n = this.state.name;
    if (!n) return;
    const v = (n.value || '').trim();
    if (!v) return;
    try {
      if (n.mode === 'newModule') {
        const created = await vcDocApi.createModule(v);
        this.setState((s) => ({
          name: null,
          open: Object.assign({}, s.open, { [created.id]: true }),
          sel: { view: 'overview' },
        }));
        await this.reloadHierarchy({ silent: true });
      } else if (n.mode === 'newTopic') {
        const created = await vcDocApi.createTopic(n.moduleId, v);
        this.setState((s) => ({
          name: null,
          open: Object.assign({}, s.open, { [n.moduleId]: true, [created.id]: true }),
          sel: { view: 'topic', moduleId: n.moduleId, topicId: created.id },
        }));
        await this.reloadHierarchy({ silent: true });
      } else {
        const ids = n.ids;
        const k = n.kind;
        if (k === 'm') await vcDocApi.renameModule(ids.moduleId, v);
        else if (k === 't') await vcDocApi.renameTopic(ids.topicId, v);
        else await vcDocApi.renameDocumentation(ids.docId, v);
        this.setState({ name: null });
        await this.reloadHierarchy({ silent: true });
      }
    } catch (e) {
      this.setState({ saved: 'Erro ao salvar' });
      alert(e.message || 'Falha ao salvar');
    }
  };

  commitDelete = async () => {
    const d = this.state.del;
    if (!d) return;
    const { kind, ids } = d;
    try {
      if (kind === 'm') await vcDocApi.deleteModule(ids.moduleId);
      else if (kind === 't') await vcDocApi.deleteTopic(ids.topicId);
      else await vcDocApi.deleteDocumentation(ids.docId);
      await this.changeSel({ view: 'overview' }, { del: null, imgBox: null, fmt: null });
      await this.reloadHierarchy({ silent: true });
    } catch (e) {
      alert(e.message || 'Falha ao excluir');
    }
  };

  addDoc = async (moduleId, topicId) => {
    try {
      const created = await vcDocApi.createDocumentation(topicId, 'Nova documentação');
      this.setState((s) => ({
        open: Object.assign({}, s.open, { [moduleId]: true, [topicId]: true }),
        sel: { view: 'doc', moduleId, topicId, docId: created.id },
      }));
      await this.reloadHierarchy({ silent: true });
      await this.ensureDocContent(created.id);
    } catch (e) {
      alert(e.message || 'Falha ao criar documentação');
    }
  };

  ensureDocContent = async (docId) => {
    const reqId = (this._docLoadSeq = (this._docLoadSeq || 0) + 1);
    try {
      const full = await vcDocApi.getDocumentation(docId);
      if (reqId !== this._docLoadSeq) return;
      const html = full.conteudoHtml || full.contentHtml || '<p></p>';
      const title = full.titulo || full.title;
      if (!this._imgGuidsByDoc) this._imgGuidsByDoc = {};
      this._imgGuidsByDoc[docId] = this.collectImgGuids(html);
      (full.imagens || []).forEach((img) => {
        if (img && img.guid) this._imgGuidsByDoc[docId].add(img.guid);
      });
      // syncEditor DEVE rodar no callback do setState — senão marca loaded
      // com html ainda null e o conteúdo salvo nunca aparece no editor.
      this.setState(
        (s) => {
          const modules = JSON.parse(JSON.stringify(s.modules));
          for (const m of modules) {
            for (const t of m.topics) {
              const d = t.docs.find((x) => x.id === docId);
              if (d) {
                d.html = html;
                if (title) d.title = title;
              }
            }
          }
          return { modules };
        },
        () => {
          if (reqId !== this._docLoadSeq) return;
          this.loaded = null;
          this.syncEditor();
        }
      );
    } catch (e) {
      /* keep empty editor */
    }
  };

  selectDoc = async (moduleId, topicId, docId) => {
    const prev = this.state.sel;
    if (prev.view === 'doc' && prev.docId !== docId) {
      await this.flushEditorSave({ quiet: true });
    }
    this.loaded = null;
    this.clearImgSelection();
    this.setState((s) => ({
      sel: { view: 'doc', moduleId, topicId, docId },
      palette: false,
      open: Object.assign({}, s.open, { [moduleId]: true, [topicId]: true }),
    }));
    this.ensureDocContent(docId);
  };

  rename(kind, ids) {
    const cur = this.find(ids);
    const obj = kind === 'm' ? cur.m : kind === 't' ? cur.t : cur.d;
    const label = kind === 'm' ? 'módulo' : kind === 't' ? 'tópico' : 'documentação';
    this.ask({
      mode: 'rename',
      kind,
      ids,
      value: kind === 'd' ? obj.title : obj.name,
      title: 'Renomear ' + label,
      sub: kind === 'd' ? cur.m.name + ' › ' + cur.t.name : kind === 't' ? cur.m.name : '1º nível',
      ph: 'Nome',
      cta: 'Salvar',
    });
  }

  remove = async (kind, ids) => {
    const cur = this.find(ids);
    let title =
      kind === 'm'
        ? 'Excluir módulo “' + cur.m.name + '”?'
        : kind === 't'
          ? 'Excluir tópico “' + cur.t.name + '”?'
          : 'Excluir “' + cur.d.title + '”?';
    let counts =
      kind === 'm'
        ? cur.m.topics.length +
          ' tópicos e ' +
          cur.m.topics.reduce((a, t) => a + t.docs.length, 0) +
          ' documentações serão removidos.'
        : kind === 't'
          ? cur.t.docs.length + ' documentações serão removidas.'
          : 'Esta página será removida do tópico ' + cur.t.name + '.';
    try {
      if (kind === 'm') {
        const imp = await vcDocApi.moduleImpact(ids.moduleId);
        counts =
          imp.topicos +
          ' tópicos e ' +
          imp.documentacoes +
          ' documentações serão removidos.';
      } else if (kind === 't') {
        const imp = await vcDocApi.topicImpact(ids.topicId);
        counts = imp.documentacoes + ' documentações serão removidas.';
      }
    } catch (_) {
      /* fallback local counts */
    }
    this.setState({
      del: { kind, ids, title, sub: counts },
      menu: null,
      imgBox: null,
      fmt: null,
      slash: null,
    });
  };

  moveTopic = async (topicId, fromModuleId, toModuleId) => {
    if (fromModuleId === toModuleId) return;
    try {
      await vcDocApi.moveTopic(topicId, toModuleId);
      await this.changeSel(
        { view: 'topic', moduleId: toModuleId, topicId },
        { open: Object.assign({}, this.state.open, { [toModuleId]: true }), move: null, menu: null }
      );
      await this.reloadHierarchy({ silent: true });
    } catch (e) {
      alert(e.message || 'Falha ao mover tópico');
    }
  };

  moveDoc = async (docId, toModuleId, toTopicId) => {
    const at = this.loc(docId);
    if (!at || at.t.id === toTopicId) return;
    try {
      const moved = await vcDocApi.moveDocumentation(docId, toTopicId);
      this.setState((s) => ({
        open: Object.assign({}, s.open, { [moved.idModulo]: true, [toTopicId]: true }),
        sel: { view: 'doc', moduleId: moved.idModulo, topicId: toTopicId, docId },
        move: null,
        menu: null,
        dropHint: null,
      }));
      await this.reloadHierarchy({ silent: true });
    } catch (e) {
      alert(e.message || 'Falha ao mover documentação');
    }
  };

  clearDropHint = () => {
    if (this.state.dropHint) this.setState({ dropHint: null });
  };

  setDropHint = (kind, id, before) => {
    const h = this.state.dropHint;
    if (h && h.kind === kind && h.id === id && !!h.before === !!before) return;
    this.setState({ dropHint: { kind, id, before: !!before } });
  };

  dropHintClass = (kind, id) => {
    const h = this.state.dropHint;
    if (!h || h.kind !== kind || h.id !== id) return '';
    return h.before ? ' is-drop-before' : ' is-drop-after';
  };

  persistModuleOrder = async (ids) => {
    const prev = this.state.modules;
    const byId = new Map(prev.map((m) => [m.id, m]));
    const modules = ids.map((id) => byId.get(id)).filter(Boolean);
    if (modules.length !== ids.length) return;
    this.setState({ modules, dropHint: null });
    try {
      await vcDocApi.reorderModules(ids);
    } catch (e) {
      this.setState({ modules: prev });
      alert(e.message || 'Falha ao reordenar módulos');
    }
  };

  persistTopicOrder = async (moduleId, ids) => {
    const prev = this.state.modules;
    const modules = prev.map((m) => {
      if (m.id !== moduleId) return m;
      const byId = new Map(m.topics.map((t) => [t.id, t]));
      return Object.assign({}, m, { topics: ids.map((id) => byId.get(id)).filter(Boolean) });
    });
    this.setState({ modules, dropHint: null });
    try {
      await vcDocApi.reorderTopics(moduleId, ids);
    } catch (e) {
      this.setState({ modules: prev });
      alert(e.message || 'Falha ao reordenar tópicos');
    }
  };

  persistDocOrder = async (moduleId, topicId, ids) => {
    const prev = this.state.modules;
    const modules = prev.map((m) => {
      if (m.id !== moduleId) return m;
      return Object.assign({}, m, {
        topics: m.topics.map((t) => {
          if (t.id !== topicId) return t;
          const byId = new Map(t.docs.map((d) => [d.id, d]));
          return Object.assign({}, t, { docs: ids.map((id) => byId.get(id)).filter(Boolean) });
        }),
      });
    });
    this.setState({ modules, dropHint: null });
    try {
      await vcDocApi.reorderDocumentations(topicId, ids);
    } catch (e) {
      this.setState({ modules: prev });
      alert(e.message || 'Falha ao reordenar documentações');
    }
  };

  onEditorInput = () => {
    const el = this.editorRef.current;
    const d = this.doc();
    if (!el || !d) return;
    const docId = d.id;
    d.html = el.innerHTML;
    d.updated = 'agora';
    this.syncRemovedImages(docId, el);
    this.setState({ saved: 'Salvando…' });
    this.scheduleEditorSave(docId);
  };

  onTitleChange = async (e) => {
    const v = e.target.value;
    const sel = this.state.sel;
    const d = this.doc();
    if (!d || sel.view !== 'doc') return;
    d.title = v;
    this.setState({ modules: this.state.modules.slice() });
    if (this.titleTimer) clearTimeout(this.titleTimer);
    this.titleTimer = setTimeout(async () => {
      try {
        await vcDocApi.renameDocumentation(d.id, v);
        await this.reloadHierarchy({ silent: true });
      } catch (_) {
        /* ignore */
      }
    }, 600);
  };

  /** Retângulo visível da seleção (fallback quando getBoundingClientRect vem 0×0 em blocos <div>). */
  selectionCaretRect = (range) => {
    if (!range) return null;
    let rect = range.getBoundingClientRect();
    if (rect.width > 0 || rect.height > 0) return rect;
    const rects = range.getClientRects();
    for (let i = 0; i < rects.length; i++) {
      if (rects[i].width > 0 || rects[i].height > 0) return rects[i];
    }
    const node = range.commonAncestorContainer;
    const block = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    if (block && block.getBoundingClientRect) return block.getBoundingClientRect();
    return rect.width || rect.height ? rect : null;
  };

  selectionInEditor = (range, el) => {
    if (!range || !el) return false;
    const node = range.commonAncestorContainer;
    if (el === node || el.contains(node)) return true;
    try {
      return el.contains(range.startContainer) && el.contains(range.endContainer);
    } catch (_) {
      return false;
    }
  };

  findLinkInSelection = (range, el) => {
    if (!range || !el) return null;
    const fromNode = (node) => {
      let cur = node;
      if (cur && cur.nodeType === Node.TEXT_NODE) cur = cur.parentElement;
      while (cur && cur !== el) {
        if (cur.tagName === 'A') {
          const href = cur.getAttribute('href');
          return href ? { href, el: cur } : null;
        }
        cur = cur.parentElement;
      }
      return null;
    };
    return (
      fromNode(range.commonAncestorContainer) ||
      fromNode(range.startContainer) ||
      fromNode(range.endContainer)
    );
  };

  queueSyncFmt = (delayMs = 0) => {
    if (this._fmtSelRaf) cancelAnimationFrame(this._fmtSelRaf);
    if (this._fmtSelTimer) clearTimeout(this._fmtSelTimer);
    const run = () => {
      this._fmtSelRaf = requestAnimationFrame(() => {
        this._fmtSelRaf = null;
        this.syncFmtFromSelection();
      });
    };
    if (delayMs > 0) this._fmtSelTimer = setTimeout(run, delayMs);
    else run();
  };

  syncFmtFromSelection = () => {
    const el = this.editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || !sel.rangeCount) {
      if (!this._fmtSelecting && this.state.fmt) {
        this.setState({ fmt: null });
        this._fmtRange = null;
      }
      return;
    }
    if (sel.isCollapsed) {
      if (this._fmtSelecting) return;
      if (this.state.fmt) {
        this.setState({ fmt: null });
        this._fmtRange = null;
      }
      return;
    }
    const range = sel.getRangeAt(0);
    const text = String(sel.toString() || '').replace(/\u00a0/g, ' ').trim();
    if (!this.selectionInEditor(range, el) || !text) {
      if (!this._fmtSelecting && this.state.fmt) {
        this.setState({ fmt: null });
        this._fmtRange = null;
      }
      return;
    }
    const r = this.selectionCaretRect(range);
    if (!r) {
      this.queueSyncFmt(16);
      return;
    }
    try {
      this._fmtRange = range.cloneRange();
    } catch (_) {
      this._fmtRange = null;
    }
    const barW = 420;
    const linkInfo = this.findLinkInSelection(range, el);
    const next = {
      top: Math.max(8, r.top - 42),
      left: Math.max(
        16,
        Math.min(r.left + Math.max(r.width, 1) / 2 - barW / 2, window.innerWidth - barW - 16)
      ),
      text,
      linkHref: linkInfo?.href || '',
    };
    const prev = this.state.fmt;
    if (
      prev &&
      Math.abs(prev.top - next.top) < 1 &&
      Math.abs(prev.left - next.left) < 1 &&
      prev.text === next.text &&
      (prev.linkHref || '') === (next.linkHref || '')
    ) {
      return;
    }
    this.setState({ fmt: next, slash: null });
  };

  onEditorPointerDown = () => {
    this._fmtSelecting = true;
  };

  onEditorPointerUp = () => {
    this._fmtSelecting = false;
    this.queueSyncFmt(0);
    this.queueSyncFmt(32);
  };

  onEditorKey = (e) => {
    if (e.key === '/') {
      const sel = window.getSelection();
      if (sel.rangeCount) {
        const r = this.selectionCaretRect(sel.getRangeAt(0));
        if (r) this.setState({ slash: { top: r.bottom + 6, left: r.left }, fmt: null });
      }
    } else if (['Escape', 'Enter', ' '].indexOf(e.key) > -1) {
      if (this.state.slash) this.setState({ slash: null });
    }

    // Copiar/colar/recortar/desfazer não alteram conteúdo — não dispara autosave
    if (e.ctrlKey || e.metaKey) {
      const k = e.key.toLowerCase();
      if (k === 'a') {
        this.queueSyncFmt(0);
        this.queueSyncFmt(32);
        return;
      }
      if (k === 'c' || k === 'x' || k === 'z' || k === 'y') return;
    }
    if (
      e.key === 'Shift' ||
      e.key === 'Control' ||
      e.key === 'Alt' ||
      e.key === 'Meta' ||
      e.key.startsWith('Arrow') ||
      e.key === 'Home' ||
      e.key === 'End' ||
      e.key === 'PageUp' ||
      e.key === 'PageDown'
    ) {
      if (e.shiftKey && (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End')) {
        this.queueSyncFmt(0);
      }
      return;
    }

    this.onEditorInput();
  };

  onEditorClick = (e) => {
    if (e.target && e.target.tagName === 'IMG') {
      this.img = e.target;
      this.measure();
      this.bindImgTrack();
    } else if (this.state.imgBox) {
      this.clearImgSelection();
    }
  };

  clearImgSelection = () => {
    this.img = null;
    this.unbindImgTrack();
    if (this.state.imgBox) this.setState({ imgBox: null });
  };

  measure = () => {
    if (!this.img || !this.img.isConnected) {
      this.clearImgSelection();
      return;
    }
    const r = this.img.getBoundingClientRect();
    this.setState({ imgBox: { top: r.top, left: r.left, w: r.width, h: r.height } });
  };

  bindImgTrack = () => {
    if (this._imgTrackBound) return;
    this._imgTrackBound = true;
    this._onImgTrack = () => {
      if (this._imgTrackRaf) cancelAnimationFrame(this._imgTrackRaf);
      this._imgTrackRaf = requestAnimationFrame(() => this.measure());
    };
    // capture: true pega scroll de .cmc-manual-scroll e outros containers
    window.addEventListener('scroll', this._onImgTrack, true);
    window.addEventListener('resize', this._onImgTrack);
  };

  unbindImgTrack = () => {
    if (!this._imgTrackBound) return;
    this._imgTrackBound = false;
    if (this._imgTrackRaf) cancelAnimationFrame(this._imgTrackRaf);
    this._imgTrackRaf = null;
    if (this._onImgTrack) {
      window.removeEventListener('scroll', this._onImgTrack, true);
      window.removeEventListener('resize', this._onImgTrack);
      this._onImgTrack = null;
    }
  };

  collectImgGuids = (htmlOrEl) => {
    const set = new Set();
    if (htmlOrEl && htmlOrEl.querySelectorAll) {
      htmlOrEl.querySelectorAll('img[data-img]').forEach((img) => {
        const g = img.getAttribute('data-img');
        if (g) set.add(g);
      });
      return set;
    }
    String(htmlOrEl || '').replace(/data-img\s*=\s*["']([^"']+)["']/gi, (_, g) => {
      if (g) set.add(g);
      return '';
    });
    return set;
  };

  syncRemovedImages = (docId, el) => {
    const now = this.collectImgGuids(el);
    const prev = this._imgGuidsByDoc && this._imgGuidsByDoc[docId]
      ? this._imgGuidsByDoc[docId]
      : new Set();
    if (!this._imgGuidsByDoc) this._imgGuidsByDoc = {};
    this._imgGuidsByDoc[docId] = now;
    const removed = [];
    prev.forEach((g) => {
      if (!now.has(g)) removed.push(g);
    });
    removed.forEach((guid) => {
      vcDocApi.deleteDocumentationImage(docId, guid).catch(() => {
        /* saveContent também limpa órfãs */
      });
    });
    if (this.img && (!this.img.isConnected || !this.img.getAttribute('data-img') || !now.has(this.img.getAttribute('data-img')))) {
      this.clearImgSelection();
    }
  };

  restoreFmtSelection = () => {
    if (!this._fmtRange) return;
    const sel = window.getSelection();
    if (!sel) return;
    try {
      sel.removeAllRanges();
      sel.addRange(this._fmtRange);
    } catch (_) {
      /* range may be stale */
    }
  };

  replaceFmtSelection = (text) => {
    const value = String(text || '');
    this.restoreFmtSelection();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const ok = document.execCommand('insertText', false, value);
    if (!ok) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const span = document.createElement('span');
      span.style.fontSize = 'inherit';
      span.style.fontFamily = 'inherit';
      span.style.lineHeight = 'inherit';
      span.style.fontWeight = 'inherit';
      span.style.color = 'inherit';
      span.textContent = value;
      range.insertNode(span);
      range.setStartAfter(span);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    this.normalizeEditorInlineFormats();
    this.onEditorInput();
    this.setState({ fmt: null });
    this._fmtRange = null;
  };

  insertFmtBelow = (text) => {
    const value = String(text || '').trim();
    if (!value) return;
    this.restoreFmtSelection();
    const sel = window.getSelection();
    const range = this._fmtRange || (sel?.rangeCount ? sel.getRangeAt(0) : null);
    if (!range) return;
    let block = range.endContainer;
    if (block.nodeType === Node.TEXT_NODE) block = block.parentElement;
    while (block && block !== this.editorRef.current) {
      const tag = String(block.tagName || '').toUpperCase();
      if (['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'LI', 'BLOCKQUOTE', 'PRE'].includes(tag)) break;
      block = block.parentElement;
    }
    const p = document.createElement('p');
    p.style.fontSize = 'inherit';
    p.style.fontFamily = 'inherit';
    p.style.lineHeight = 'inherit';
    p.style.fontWeight = 'inherit';
    p.style.color = 'inherit';
    p.textContent = value;
    if (block && block.parentNode) {
      if (block.nextSibling) block.parentNode.insertBefore(p, block.nextSibling);
      else block.parentNode.appendChild(p);
    } else if (this.editorRef.current) {
      this.editorRef.current.appendChild(p);
    }
    this.onEditorInput();
    this.setState({ fmt: null });
    this._fmtRange = null;
  };

  exec(cmd, val) {
    this.restoreFmtSelection();
    document.execCommand(cmd, false, val || null);
    this.normalizeEditorInlineFormats();
    this.onEditorInput();
    this.setState({ fmt: null, slash: null });
    this._fmtRange = null;
  }

  /** Chromium grava B/I como <span style="font-weight/style"> — vira <b>/<i> semânticos. */
  normalizeEditorInlineFormats = () => {
    const el = this.editorRef.current;
    if (!el) return;
    const styled = el.querySelectorAll('span[style], font[style], font[face], font[size], font[color]');
    styled.forEach((node) => {
      const style = String(node.getAttribute('style') || '').toLowerCase();
      const fw = ((style.match(/font-weight\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const fs = ((style.match(/font-style\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const td = ((style.match(/text-decoration(?:-line)?\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const wraps = [];
      if (/bold|[6-9]00/.test(fw)) wraps.push('b');
      if (/italic/.test(fs)) wraps.push('i');
      if (/underline/.test(td)) wraps.push('u');
      if (/line-through/.test(td)) wraps.push('s');

      const parent = node.parentNode;
      if (!parent) return;

      if (!wraps.length) {
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
        return;
      }

      let wrap = document.createElement(wraps[0]);
      let innermost = wrap;
      for (let i = 1; i < wraps.length; i++) {
        const next = document.createElement(wraps[i]);
        innermost.appendChild(next);
        innermost = next;
      }
      while (node.firstChild) innermost.appendChild(node.firstChild);
      parent.insertBefore(wrap, node);
      parent.removeChild(node);
    });

    // spans vazios / sem estilo remanescentes
    el.querySelectorAll('span:not([data-img])').forEach((span) => {
      if (span.hasAttribute('style') || span.hasAttribute('class')) return;
      const parent = span.parentNode;
      if (!parent) return;
      while (span.firstChild) parent.insertBefore(span.firstChild, span);
      parent.removeChild(span);
    });
  };

  insertImage = () => {
    const d = this.doc();
    const el = this.editorRef.current;
    if (!d || !el) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif';
    input.style.display = 'none';
    input.onchange = async () => {
      const file = input.files && input.files[0];
      try {
        input.remove();
      } catch (_) {
        /* ignore */
      }
      if (!file) return;
      await this.uploadAndInsertImage(file);
    };
    document.body.appendChild(input);
    input.click();
  };

  /** Cola imagem OU texto limpo (sem fonte/tamanho/cor do Word/web). */
  onEditorPaste = (e) => {
    const d = this.doc();
    if (!d) return;
    const cd = e.clipboardData;
    if (!cd) return;

    let file = null;
    if (cd.files && cd.files.length) {
      for (let i = 0; i < cd.files.length; i++) {
        const f = cd.files[i];
        if (f && String(f.type || '').startsWith('image/')) {
          file = f;
          break;
        }
      }
    }
    if (!file && cd.items && cd.items.length) {
      for (let i = 0; i < cd.items.length; i++) {
        const item = cd.items[i];
        if (item && item.kind === 'file' && String(item.type || '').startsWith('image/')) {
          file = item.getAsFile();
          break;
        }
      }
    }
    if (file) {
      e.preventDefault();
      e.stopPropagation();
      this.uploadAndInsertImage(file);
      return;
    }

    // Texto/HTML: sempre normalizar ao padrão do editor
    e.preventDefault();
    e.stopPropagation();

    const htmlRaw = cd.getData('text/html');
    const textRaw = cd.getData('text/plain');
    let insert = '';
    if (htmlRaw && /<[a-z][\s\S]*>/i.test(htmlRaw)) {
      insert = normalizePasteHtml(htmlRaw);
    } else {
      insert = normalizePastePlainText(textRaw);
    }
    if (!insert) return;

    document.execCommand('insertHTML', false, insert);
    this.onEditorInput();
    this.setState({ slash: null, fmt: null });
  };

  uploadAndInsertImage = async (file) => {
    const d = this.doc();
    const el = this.editorRef.current;
    if (!d || !el || !file) return;

    const mime = String(file.type || '').toLowerCase().split(';')[0].trim();
    const allowed = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']);
    if (!allowed.has(mime)) {
      alert('Tipo de imagem não permitido. Use JPG, PNG, WEBP ou GIF.');
      return;
    }

    let uploadFile = file;
    if (!file.name || !String(file.name).trim()) {
      const ext =
        mime === 'image/jpeg' || mime === 'image/jpg'
          ? 'jpg'
          : mime === 'image/png'
            ? 'png'
            : mime === 'image/webp'
              ? 'webp'
              : mime === 'image/gif'
                ? 'gif'
                : 'png';
      uploadFile = new File([file], `paste-${Date.now()}.${ext}`, { type: mime === 'image/jpg' ? 'image/jpeg' : mime });
    }

    // preserva caret antes do await
    el.focus();
    const sel = window.getSelection();
    let range = null;
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) range = r.cloneRange();
    }

    try {
      this.setState({ saved: 'Enviando imagem…', slash: null });
      const uploaded = await vcDocApi.uploadDocumentationImage(d.id, uploadFile);
      const guid = uploaded.guid;
      const src = uploaded.viewUrl;
      if (!guid || !src) throw new Error('Resposta de upload incompleta.');

      el.focus();
      if (range) {
        const s2 = window.getSelection();
        if (s2) {
          s2.removeAllRanges();
          s2.addRange(range);
        }
      }
      document.execCommand(
        'insertHTML',
        false,
        `<img data-img="${guid}" src="${src}" style="width: 100%;" /><p></p>`
      );
      if (!this._imgGuidsByDoc) this._imgGuidsByDoc = {};
      const set = this._imgGuidsByDoc[d.id] || new Set();
      set.add(guid);
      this._imgGuidsByDoc[d.id] = set;
      this.onEditorInput();
      this.setState({ saved: 'Salvo agora' });
    } catch (err) {
      alert(err.message || 'Falha ao inserir imagem');
      this.setState({ saved: 'Erro ao salvar' });
    }
  };

  startResize = (dir) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const img = this.img;
    if (!img) return;
    const startX = e.clientX;
    const startW = img.getBoundingClientRect().width;
    const wrapW = this.editorRef.current.getBoundingClientRect().width;
    const fromLeft = dir === 'tl' || dir === 'bl';
    const move = (ev) => {
      const dx = (ev.clientX - startX) * (fromLeft ? -1 : 1);
      const w = Math.max(120, Math.min(wrapW, startW + dx));
      img.style.width = Math.round((w / wrapW) * 100) + '%';
      this.measure();
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      this.onEditorInput();
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  removeSelectedImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const img = this.img;
    const d = this.doc();
    if (!img || !d) return;
    const guid = img.getAttribute('data-img');
    if (img.parentNode) img.parentNode.removeChild(img);
    this.clearImgSelection();
    if (guid) {
      if (!this._imgGuidsByDoc) this._imgGuidsByDoc = {};
      const set = this._imgGuidsByDoc[d.id] || new Set();
      set.delete(guid);
      this._imgGuidsByDoc[d.id] = set;
      vcDocApi.deleteDocumentationImage(d.id, guid).catch(() => {
        /* autosave também limpa órfãs */
      });
    }
    this.onEditorInput();
  };

  closeAll = () =>
    this.setState({
      menu: null,
      move: null,
      palette: false,
      slash: null,
      fmt: null,
      name: null,
      del: null,
      backup: null,
    });

  openBackupModal = () => {
    this.setState({
      backup: { busy: null, error: null, info: null, pending: null },
      menu: null,
      move: null,
      palette: false,
      slash: null,
      fmt: null,
      imgBox: null,
      name: null,
      del: null,
    });
  };

  runExportBackup = async () => {
    if (this.state.backup?.busy) return;
    this.setState((p) => ({
      backup: Object.assign({}, p.backup, { busy: 'export', error: null, info: null }),
    }));
    try {
      await this.flushEditorSave({ quiet: true });
      const data = await vcDocApi.exportDocumentationBackup();
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vc-doc-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      this.setState((p) => ({
        backup: Object.assign({}, p.backup, {
          busy: null,
          info: `Backup gerado (${data?.counts?.documentacoes ?? 0} docs, ${data?.counts?.images ?? 0} imagens).`,
        }),
      }));
    } catch (e) {
      this.setState((p) => ({
        backup: Object.assign({}, p.backup, {
          busy: null,
          error: e.message || 'Falha ao exportar backup.',
        }),
      }));
    }
  };

  onBackupFilePicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || this.state.backup?.busy) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || parsed.format !== 'vc_doc_backup_v1') {
        throw new Error('Arquivo não é um backup válido deste módulo.');
      }
      this.setState((p) => ({
        backup: Object.assign({}, p.backup, {
          pending: parsed,
          error: null,
          info: null,
        }),
      }));
    } catch (err) {
      this.setState((p) => ({
        backup: Object.assign({}, p.backup, {
          pending: null,
          error: err.message || 'Não foi possível ler o arquivo.',
        }),
      }));
    }
  };

  runImportBackup = async () => {
    const pending = this.state.backup?.pending;
    if (!pending || this.state.backup?.busy) return;
    this.setState((p) => ({
      backup: Object.assign({}, p.backup, { busy: 'import', error: null, info: null }),
    }));
    try {
      await this.flushEditorSave({ quiet: true });
      const data = await vcDocApi.importDocumentationBackup(pending);
      this.loaded = null;
      this.setState({
        backup: {
          busy: null,
          pending: null,
          error: null,
          info: `Restauração concluída (${data?.counts?.documentacoes ?? 0} docs).`,
        },
        sel: { view: 'overview' },
        modules: [],
        open: {},
        imgBox: null,
        fmt: null,
        slash: null,
      });
      await this.reloadHierarchy();
    } catch (e) {
      this.setState((p) => ({
        backup: Object.assign({}, p.backup, {
          busy: null,
          error: e.message || 'Falha ao restaurar backup.',
        }),
      }));
    }
  };

  stop = (e) => e.stopPropagation();

  renderVals() {
    const s = this.state;
    const sel = s.sel;
    const ms = s.modules;
    const q = s.query.trim().toLowerCase();

    const tree = ms.map((m) => {
      const docCount = m.topics.reduce((a, t) => a + t.docs.length, 0);
      return {
        id: m.id,
        name: m.name,
        open: !!s.open[m.id],
        rot: s.open[m.id] ? '90deg' : '0deg',
        count: docCount,
        empty: m.topics.length === 0,
        bg: sel.moduleId === m.id && sel.view !== 'doc' && !sel.topicId ? 'var(--cmc-hover)' : 'transparent',
        onToggle: () =>
          this.setState((p) => ({ open: Object.assign({}, p.open, { [m.id]: !p.open[m.id] }) })),
        onAdd: (e) => {
          e.stopPropagation();
          this.addTopic(m.id);
        },
        onMenu: (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.setState({
            menu: {
              kind: 'm',
              ids: { moduleId: m.id },
              top: e.clientY,
              left: Math.min(e.clientX, window.innerWidth - 220),
            },
          });
        },
        dropClass: this.dropHintClass('m', m.id),
        onDragStart: (e) => {
          if (e.target.closest && e.target.closest('button')) {
            e.preventDefault();
            return;
          }
          this.drag = { kind: 'm', moduleId: m.id };
          e.stopPropagation();
        },
        onDragOver: (e) => {
          const d = this.drag;
          if (!d) return;
          if (d.kind === 'm' || d.kind === 't') {
            e.preventDefault();
            e.stopPropagation();
            if (d.kind === 'm' && d.moduleId !== m.id) {
              this.setDropHint('m', m.id, dropBeforeFromEvent(e));
            } else if (d.kind === 't') {
              this.clearDropHint();
            }
          }
        },
        onDragLeave: () => this.clearDropHint(),
        onDrop: (e) => {
          e.preventDefault();
          e.stopPropagation();
          const d = this.drag;
          this.drag = null;
          const before = dropBeforeFromEvent(e);
          this.clearDropHint();
          if (!d) return;
          if (d.kind === 'm' && d.moduleId !== m.id) {
            const ids = reorderIds(this.state.modules, d.moduleId, m.id, before);
            if (ids) this.persistModuleOrder(ids);
            return;
          }
          if (d.kind === 't' && d.moduleId !== m.id) {
            this.moveTopic(d.topicId, d.moduleId, m.id);
          }
        },
        onDragEnd: () => {
          this.drag = null;
          this.clearDropHint();
        },
        topics: m.topics.map((t) => ({
          id: t.id,
          name: t.name,
          open: !!s.open[t.id],
          rot: s.open[t.id] ? '90deg' : '0deg',
          empty: t.docs.length === 0,
          bg: sel.topicId === t.id && sel.view === 'topic' ? 'var(--cmc-hover)' : 'transparent',
          fg: sel.topicId === t.id ? 'var(--cmc-text)' : 'var(--cmc-text-2)',
          fw: sel.topicId === t.id ? 600 : 500,
          dropClass: this.dropHintClass('t', t.id),
          onSelect: () =>
            this.changeSel(
              { view: 'topic', moduleId: m.id, topicId: t.id },
              { open: Object.assign({}, this.state.open, { [m.id]: true, [t.id]: true }) }
            ),
          onToggle: (e) => {
            e.stopPropagation();
            this.setState((p) => ({ open: Object.assign({}, p.open, { [t.id]: !p.open[t.id] }) }));
          },
          onAdd: (e) => {
            e.stopPropagation();
            this.addDoc(m.id, t.id);
          },
          onMenu: (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.setState({
              menu: {
                kind: 't',
                ids: { moduleId: m.id, topicId: t.id },
                top: e.clientY,
                left: Math.min(e.clientX, window.innerWidth - 220),
              },
            });
          },
          onDragStart: (e) => {
            if (e.target.closest && e.target.closest('button')) {
              e.preventDefault();
              return;
            }
            this.drag = { kind: 't', topicId: t.id, moduleId: m.id };
            e.stopPropagation();
          },
          onDragOver: (e) => {
            const d = this.drag;
            if (!d) return;
            if (d.kind === 't' || d.kind === 'd') {
              e.preventDefault();
              e.stopPropagation();
              if (d.kind === 't' && d.moduleId === m.id && d.topicId !== t.id) {
                this.setDropHint('t', t.id, dropBeforeFromEvent(e));
              } else if (d.kind === 'd') {
                this.clearDropHint();
              }
            }
          },
          onDrop: (e) => {
            e.preventDefault();
            e.stopPropagation();
            const d = this.drag;
            this.drag = null;
            const before = dropBeforeFromEvent(e);
            this.clearDropHint();
            if (!d) return;
            if (d.kind === 't') {
              if (d.moduleId === m.id && d.topicId !== t.id) {
                const ids = reorderIds(m.topics, d.topicId, t.id, before);
                if (ids) this.persistTopicOrder(m.id, ids);
              } else if (d.moduleId !== m.id) {
                this.moveTopic(d.topicId, d.moduleId, m.id);
              }
              return;
            }
            if (d.kind === 'd') this.moveDoc(d.docId, m.id, t.id);
          },
          onDragEnd: () => {
            this.drag = null;
            this.clearDropHint();
          },
          docs: t.docs.map((d) => ({
            id: d.id,
            title: d.title,
            bg: sel.docId === d.id ? 'var(--cmc-accent-sel)' : 'transparent',
            fg: sel.docId === d.id ? 'var(--cmc-accent-sel-fg)' : 'var(--cmc-text-3)',
            fw: sel.docId === d.id ? 600 : 400,
            dot: sel.docId === d.id ? 'var(--cmc-accent-dot)' : '#c8cad0',
            dropClass: this.dropHintClass('d', d.id),
            onSelect: () => this.selectDoc(m.id, t.id, d.id),
            onMenu: (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.setState({
                menu: {
                  kind: 'd',
                  ids: { moduleId: m.id, topicId: t.id, docId: d.id },
                  top: e.clientY,
                  left: Math.min(e.clientX, window.innerWidth - 220),
                },
              });
            },
            onDragStart: (e) => {
              if (e.target.closest && e.target.closest('button')) {
                e.preventDefault();
                return;
              }
              this.drag = { kind: 'd', docId: d.id, topicId: t.id, moduleId: m.id };
              e.stopPropagation();
            },
            onDragOver: (e) => {
              const dr = this.drag;
              if (!dr || dr.kind !== 'd') return;
              e.preventDefault();
              e.stopPropagation();
              if (dr.docId !== d.id) this.setDropHint('d', d.id, dropBeforeFromEvent(e));
            },
            onDrop: (e) => {
              e.preventDefault();
              e.stopPropagation();
              const dr = this.drag;
              this.drag = null;
              const before = dropBeforeFromEvent(e);
              this.clearDropHint();
              if (!dr || dr.kind !== 'd' || dr.docId === d.id) return;
              if (dr.topicId === t.id) {
                const ids = reorderIds(t.docs, dr.docId, d.id, before);
                if (ids) this.persistDocOrder(m.id, t.id, ids);
              } else {
                this.moveDoc(dr.docId, m.id, t.id);
              }
            },
            onDragEnd: () => {
              this.drag = null;
              this.clearDropHint();
            },
          })),
        })),
      };
    });

    const allDocs = [];
    ms.forEach((m) => m.topics.forEach((t) => t.docs.forEach((d) => allDocs.push({ m, t, d }))));
    const totalTopics = ms.reduce((a, m) => a + m.topics.length, 0);
    const emptyTopics = ms.reduce((a, m) => a + m.topics.filter((t) => !t.docs.length).length, 0);

    const cards = ms.map((m) => {
      const dc = m.topics.reduce((a, t) => a + t.docs.length, 0);
      const covered = m.topics.filter((t) => t.docs.length).length;
      return {
        name: m.name,
        initial: m.name.slice(0, 1).toUpperCase(),
        empty: !m.topics.length,
        pct: (m.topics.length ? Math.round((covered / m.topics.length) * 100) : 0) + '%',
        meta: m.topics.length + ' tóp · ' + dc + ' docs',
        onOpen: () =>
          this.changeSel(
            { view: 'overview' },
            { open: Object.assign({}, this.state.open, { [m.id]: true }) }
          ),
        onRename: (e) => {
          e.stopPropagation();
          this.rename('m', { moduleId: m.id });
        },
        onDelete: (e) => {
          e.stopPropagation();
          this.remove('m', { moduleId: m.id });
        },
        onAddTopic: (e) => {
          e.stopPropagation();
          this.addTopic(m.id);
        },
        onDragOver: (e) => {
          if (this.drag && this.drag.kind === 't') e.preventDefault();
        },
        onDrop: (e) => {
          e.preventDefault();
          if (this.drag && this.drag.kind === 't')
            this.moveTopic(this.drag.topicId, this.drag.moduleId, m.id);
          this.drag = null;
        },
        topics: m.topics.map((t) => ({
          name: t.name,
          count: t.docs.length,
          onOpen: () =>
            this.changeSel(
              { view: 'topic', moduleId: m.id, topicId: t.id },
              { open: Object.assign({}, this.state.open, { [m.id]: true, [t.id]: true }) }
            ),
        })),
      };
    });

    const openDoc = (m, t, d) => () => this.selectDoc(m.id, t.id, d.id);

    const cur = this.find(sel);
    const crumbs = [
      {
        label: 'Manual',
        onClick: () => this.changeSel({ view: 'overview' }),
        fg: 'var(--cmc-muted-2)',
        fw: 500,
        sep: cur.m ? '›' : '',
      },
    ];
    if (cur.m)
      crumbs.push({
        label: cur.m.name,
        onClick: () => this.changeSel({ view: 'overview' }),
        fg: cur.t ? 'var(--cmc-muted-2)' : 'var(--cmc-text)',
        fw: cur.t ? 500 : 600,
        sep: cur.t ? '›' : '',
      });
    if (cur.t)
      crumbs.push({
        label: cur.t.name,
        onClick: () =>
          this.changeSel({ view: 'topic', moduleId: cur.m.id, topicId: cur.t.id }),
        fg: cur.d ? 'var(--cmc-muted-2)' : 'var(--cmc-text)',
        fw: cur.d ? 500 : 600,
        sep: cur.d ? '›' : '',
      });
    if (cur.d)
      crumbs.push({ label: cur.d.title, onClick: () => {}, fg: 'var(--cmc-text)', fw: 600, sep: '' });

    const menuItems = [];
    if (s.menu) {
      const k = s.menu.kind;
      const ids = s.menu.ids;
      if (k === 'm')
        menuItems.push({
          icon: '+',
          label: 'Novo tópico',
          fg: 'var(--cmc-text)',
          onClick: () => this.addTopic(ids.moduleId),
        });
      if (k === 't') {
        menuItems.push({
          icon: '+',
          label: 'Nova documentação',
          fg: 'var(--cmc-text)',
          onClick: () => this.addDoc(ids.moduleId, ids.topicId),
        });
        menuItems.push({
          icon: '→',
          label: 'Mover para módulo…',
          fg: 'var(--cmc-text)',
          onClick: () => this.setState({ move: { kind: 't', ids }, menu: null }),
        });
      }
      if (k === 'd')
        menuItems.push({
          icon: '→',
          label: 'Mover para tópico…',
          fg: 'var(--cmc-text)',
          onClick: () => this.setState({ move: { kind: 'd', ids }, menu: null }),
        });
      menuItems.push({
        icon: <Pencil size={14} strokeWidth={1.75} aria-hidden />,
        label: 'Renomear',
        fg: 'var(--cmc-text)',
        onClick: () => this.rename(k, ids),
      });
      menuItems.push({
        icon: <Trash2 size={14} strokeWidth={1.75} aria-hidden />,
        label: 'Excluir',
        fg: 'var(--cmc-danger)',
        onClick: () => this.remove(k, ids),
      });
    }

    const mv = s.move;
    let moveTargets = [];
    if (mv && mv.kind === 't') {
      const from = this.find(mv.ids);
      moveTargets = ms.map((m) => ({
        label: m.name,
        initial: m.name.slice(0, 1).toUpperCase(),
        sub: m.id === from.m.id ? 'módulo atual' : m.topics.length + ' tópicos',
        cursor: m.id === from.m.id ? 'default' : 'pointer',
        opacity: m.id === from.m.id ? 0.45 : 1,
        onClick: () => this.moveTopic(mv.ids.topicId, from.m.id, m.id),
      }));
    } else if (mv && mv.kind === 'd') {
      const from = this.find(mv.ids);
      ms.forEach((m) =>
        m.topics.forEach((t) =>
          moveTargets.push({
            label: t.name,
            initial: m.name.slice(0, 1).toUpperCase(),
            sub: m.name + (t.id === from.t.id ? ' · atual' : ''),
            cursor: t.id === from.t.id ? 'default' : 'pointer',
            opacity: t.id === from.t.id ? 0.45 : 1,
            onClick: () => this.moveDoc(mv.ids.docId, m.id, t.id),
          })
        )
      );
    }

    let results = [];
    if (q) {
      ms.forEach((m) => {
        if (m.name.toLowerCase().includes(q))
          results.push({
            kind: 'módulo',
            label: m.name,
            path: m.topics.length + ' tópicos',
            onClick: () =>
              this.changeSel(
                { view: 'overview' },
                { palette: false, open: Object.assign({}, this.state.open, { [m.id]: true }) }
              ),
          });
        m.topics.forEach((t) => {
          if (t.name.toLowerCase().includes(q))
            results.push({
              kind: 'tópico',
              label: t.name,
              path: m.name,
              onClick: () =>
                this.changeSel(
                  { view: 'topic', moduleId: m.id, topicId: t.id },
                  {
                    palette: false,
                    open: Object.assign({}, this.state.open, { [m.id]: true, [t.id]: true }),
                  }
                ),
            });
          t.docs.forEach((d) => {
            if ((d.title + ' ' + d.html).toLowerCase().includes(q))
              results.push({
                kind: 'doc',
                label: d.title,
                path: m.name + ' › ' + t.name,
                onClick: openDoc(m, t, d),
              });
          });
        });
      });
      results = results.slice(0, 30);
    } else {
      results = allDocs.slice(0, 8).map(({ m, t, d }) => ({
        kind: 'doc',
        label: d.title,
        path: m.name + ' › ' + t.name,
        onClick: openDoc(m, t, d),
      }));
    }

    return {
      tree,
      treeEmpty: ms.length === 0,
      totalDocs: allDocs.length,
      totalModules: ms.length,
      crumbs,
      savedLabel: s.saved,
      isOverview: sel.view === 'overview',
      isTopic: sel.view === 'topic',
      isDoc: sel.view === 'doc',
      showMetrics: true,
      metrics: [
        { label: 'Módulos', value: s.metricas?.modulos ?? ms.length, hint: '1º nível' },
        { label: 'Tópicos', value: s.metricas?.topicos ?? totalTopics, hint: '2º nível' },
        { label: 'Documentações', value: s.metricas?.documentacoes ?? allDocs.length, hint: '3º nível' },
        { label: 'Sem conteúdo', value: s.metricas?.topicosVazios ?? emptyTopics, hint: 'tópicos vazios' },
      ],
      cards,
      topicName: cur.t ? cur.t.name : '',
      topicModuleName: cur.m ? cur.m.name : '',
      topicEmpty: !!cur.t && cur.t.docs.length === 0,
      topicDocs: cur.t
        ? cur.t.docs.map((d) => ({
            title: d.title,
            excerpt: strip(d.html) || (d.excerpt || '').trim() || 'Sem conteúdo ainda.',
            when: 'editado ' + (d.updated || 'agora') + ' atrás',
            onOpen: () => this.selectDoc(cur.m.id, cur.t.id, d.id),
          }))
        : [],
      addDocHere: () => cur.t && this.addDoc(cur.m.id, cur.t.id),
      docTitle: cur.d ? cur.d.title : '',
      docPath: cur.d ? cur.m.name + ' › ' + cur.t.name : '',
      onTitle: this.onTitleChange,
      menuItems,
      moveTargets,
      mv,
      results,
      noResults: !!q && results.length === 0,
    };
  }

  renderOverlays(v) {
    const s = this.state;
    const nodes = [];

    if (s.imgBox) {
      const b = s.imgBox;
      nodes.push(
        createPortal(
          <div
            key="img"
            className="cmc-manual-img-box"
            style={{ top: b.top, left: b.left, width: b.w, height: b.h }}
          >
            <div
              className="cmc-manual-img-handle cmc-manual-img-handle--tl"
              onMouseDown={this.startResize('tl')}
            />
            <div
              className="cmc-manual-img-handle cmc-manual-img-handle--tr"
              onMouseDown={this.startResize('tr')}
            />
            <div
              className="cmc-manual-img-handle cmc-manual-img-handle--bl"
              onMouseDown={this.startResize('bl')}
            />
            <div
              className="cmc-manual-img-handle cmc-manual-img-handle--br"
              onMouseDown={this.startResize('br')}
            />
            <div className="cmc-manual-img-size">
              {Math.round(b.w)} × {Math.round(b.h)}
            </div>
            <Tip label="Excluir imagem" placement="top">
              <button
                type="button"
                className="cmc-manual-img-delete"
                aria-label="Excluir imagem"
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                }}
                onClick={this.removeSelectedImage}
              >
                Excluir
              </button>
            </Tip>
          </div>,
          document.body
        )
      );
    }

    if (s.fmt) {
      nodes.push(
        createPortal(
          <CustomersNoticeFmtBar
            key="fmt"
            position={{ top: s.fmt.top, left: s.fmt.left }}
            selectedText={s.fmt.text || ''}
            linkHref={s.fmt.linkHref || ''}
            onExec={(cmd, val) => this.exec(cmd, val)}
            onReplaceSelection={this.replaceFmtSelection}
            onInsertBelow={this.insertFmtBelow}
            onClose={() => {
              this.setState({ fmt: null });
              this._fmtRange = null;
            }}
          />,
          document.body
        )
      );
    }

    if (s.slash) {
      const items = [
        { label: 'Título 1', hint: 'Seção principal', run: () => this.exec('formatBlock', 'H1') },
        { label: 'Título 2', hint: 'Subseção', run: () => this.exec('formatBlock', 'H2') },
        { label: 'Lista', hint: 'Passo a passo', run: () => this.exec('insertUnorderedList') },
        { label: 'Lista numerada', hint: 'Sequência', run: () => this.exec('insertOrderedList') },
        { label: 'Imagem', hint: 'Redimensionável', run: this.insertImage },
        { label: 'Código', hint: 'Bloco monoespaçado', run: () => this.exec('formatBlock', 'PRE') },
        { label: 'Aviso', hint: 'Citação destacada', run: () => this.exec('formatBlock', 'BLOCKQUOTE') },
        { label: 'Divisor', hint: 'Linha horizontal', run: () => this.exec('insertHorizontalRule') },
      ];
      nodes.push(
        createPortal(
          <div
            key="slash"
            className="cmc-manual-slash"
            style={{ top: Math.min(s.slash.top, window.innerHeight - 330), left: s.slash.left }}
          >
            <div className="cmc-manual-slash-title">Inserir bloco</div>
            {items.map((it) => (
              <div
                key={it.label}
                className="cmc-manual-slash-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  this.setState({ slash: null });
                  it.run();
                }}
              >
                <span>{it.label}</span>
                <span>{it.hint}</span>
              </div>
            ))}
          </div>,
          document.body
        )
      );
    }

    if (s.menu) {
      nodes.push(
        createPortal(
          <div
            key="menu"
            className="cmc-manual-overlay"
            onClick={this.closeAll}
            onContextMenu={(e) => {
              e.preventDefault();
              this.closeAll();
            }}
          >
            <div className="cmc-manual-ctx" style={{ top: s.menu.top, left: s.menu.left }} onClick={this.stop}>
              {v.menuItems.map((i) => (
                <div
                  key={i.label}
                  className="cmc-manual-ctx-item"
                  style={{ color: i.fg }}
                  onClick={(e) => {
                    e.stopPropagation();
                    i.onClick(e);
                  }}
                >
                  <span className="cmc-manual-ctx-icon">{i.icon}</span>
                  {i.label}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )
      );
    }

    if (s.move) {
      nodes.push(
        createPortal(
          <div key="move" className="cmc-manual-modal-scrim" onClick={this.closeAll}>
            <div className="cmc-manual-modal" onClick={this.stop}>
              <div className="cmc-manual-modal-head">
                <div className="cmc-manual-modal-title">
                  {s.move.kind === 't' ? 'Mover tópico para outro módulo' : 'Mover documentação para outro tópico'}
                </div>
                <div className="cmc-manual-modal-sub">
                  {s.move.kind === 't'
                    ? 'O tópico leva consigo todas as documentações.'
                    : 'Escolha o tópico de destino — o módulo aparece ao lado.'}
                </div>
              </div>
              <div className="cmc-manual-modal-body">
                {v.moveTargets.map((o, idx) => (
                  <div
                    key={idx}
                    className="cmc-manual-move-row"
                    style={{ cursor: o.cursor, opacity: o.opacity }}
                    onClick={o.onClick}
                  >
                    <span className="cmc-manual-move-initial">{o.initial}</span>
                    <span className="cmc-manual-move-label">{o.label}</span>
                    <span className="cmc-manual-move-sub">{o.sub}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )
      );
    }

    if (s.name) {
      nodes.push(
        createPortal(
          <div
            key="name"
            className="cmc-manual-modal-scrim cmc-manual-modal-scrim--name"
            onClick={() => this.setState({ name: null, del: null })}
          >
            <div className="cmc-manual-modal cmc-manual-modal--sm" onClick={this.stop}>
              <div className="cmc-manual-modal-title">{s.name.title}</div>
              <div className="cmc-manual-modal-sub">{s.name.sub}</div>
              <input
                ref={this.nameRef}
                className="cmc-manual-input"
                value={s.name.value}
                placeholder={s.name.ph}
                onChange={(e) => {
                  const val = e.target.value;
                  this.setState((p) => ({ name: Object.assign({}, p.name, { value: val }) }));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    this.commitName();
                  }
                }}
              />
              <div className="cmc-manual-modal-actions">
                <button
                  type="button"
                  className="user-form-link-btn user-form-footer-btn"
                  onClick={() => this.setState({ name: null, del: null })}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="user-form-profile-save-btn user-form-footer-btn"
                  onClick={this.commitName}
                >
                  {s.name.cta}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      );
    }

    if (s.del) {
      nodes.push(
        createPortal(
          <div
            key="del"
            className="cmc-manual-modal-scrim cmc-manual-modal-scrim--del"
            onClick={() => this.setState({ name: null, del: null })}
          >
            <div className="cmc-manual-modal cmc-manual-modal--sm" onClick={this.stop}>
              <div className="cmc-manual-modal-title">{s.del.title}</div>
              <div className="cmc-manual-modal-sub--del">
                {s.del.sub} Esta ação não pode ser desfeita.
              </div>
              <div className="cmc-manual-modal-actions">
                <button
                  type="button"
                  className="user-form-link-btn user-form-footer-btn"
                  onClick={() => this.setState({ name: null, del: null })}
                >
                  Cancelar
                </button>
                <button type="button" className="cmc-manual-danger-btn" onClick={this.commitDelete}>
                  Excluir
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      );
    }

    if (s.backup) {
      const b = s.backup;
      const busy = !!b.busy;
      nodes.push(
        createPortal(
          <div
            key="backup"
            className="cmc-manual-modal-scrim cmc-manual-modal-scrim--backup"
            onClick={() => {
              if (!busy) this.setState({ backup: null });
            }}
          >
            <div className="cmc-manual-modal cmc-manual-modal--backup" onClick={this.stop}>
              <div className="cmc-manual-modal-head">
                <div>
                  <div className="cmc-manual-modal-title">Backup da documentação</div>
                  <div className="cmc-manual-modal-sub">
                    Exporta ou restaura toda a documentação deste cliente. O arquivo é portável e pode ser
                    restaurado em qualquer outro cliente.
                  </div>
                </div>
              </div>
              <div className="cmc-manual-modal-body cmc-manual-backup-body">
                <div className="cmc-manual-backup-stat">
                  Conteúdo atual: {v.totalDocs} páginas · {v.totalModules} módulos
                </div>

                <div className="cmc-manual-backup-block">
                  <div className="cmc-manual-backup-block-title">Exportar</div>
                  <p className="cmc-manual-backup-hint">
                    Gera um arquivo JSON com módulos, tópicos, páginas, versões e imagens.
                  </p>
                  <button
                    type="button"
                    className="cmc-manual-primary-btn cmc-manual-primary-btn--inline"
                    disabled={busy}
                    onClick={this.runExportBackup}
                  >
                    {b.busy === 'export' ? 'Gerando…' : 'Baixar backup'}
                  </button>
                </div>

                <div className="cmc-manual-backup-block">
                  <div className="cmc-manual-backup-block-title">Restaurar</div>
                  <p className="cmc-manual-backup-hint">
                    Substitui toda a documentação atual pelo conteúdo do arquivo. Não pode ser desfeito.
                  </p>
                  {!b.pending ? (
                    <label className={'cmc-manual-backup-file' + (busy ? ' is-disabled' : '')}>
                      <input
                        type="file"
                        accept="application/json,.json"
                        disabled={busy}
                        onChange={this.onBackupFilePicked}
                      />
                      Escolher arquivo…
                    </label>
                  ) : (
                    <div className="cmc-manual-backup-pending">
                      <div className="cmc-manual-backup-pending-meta">
                        {(b.pending.counts && b.pending.counts.documentations) || 0} docs
                        {' · '}
                        {(b.pending.counts && b.pending.counts.images) || 0} imagens
                        {' · '}
                        {(b.pending.counts && b.pending.counts.modules) || 0} módulos
                      </div>
                      <div className="cmc-manual-modal-actions" style={{ marginTop: 10 }}>
                        <button
                          type="button"
                          className="cmc-manual-ghost-btn cmc-manual-ghost-btn--md"
                          disabled={busy}
                          onClick={() =>
                            this.setState((p) => ({
                              backup: Object.assign({}, p.backup, { pending: null }),
                            }))
                          }
                        >
                          Trocar arquivo
                        </button>
                        <button
                          type="button"
                          className="cmc-manual-danger-btn"
                          disabled={busy}
                          onClick={this.runImportBackup}
                        >
                          {b.busy === 'import' ? 'Restaurando…' : 'Confirmar restauração'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {b.error ? <div className="cmc-manual-backup-msg cmc-manual-backup-msg--err">{b.error}</div> : null}
                {b.info ? <div className="cmc-manual-backup-msg cmc-manual-backup-msg--ok">{b.info}</div> : null}
              </div>
              <div className="cmc-manual-modal-actions">
                <button
                  type="button"
                  className="cmc-manual-ghost-btn cmc-manual-ghost-btn--md"
                  disabled={busy}
                  onClick={() => this.setState({ backup: null })}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      );
    }

    if (s.palette) {
      nodes.push(
        createPortal(
          <div key="palette" className="cmc-manual-modal-scrim cmc-manual-modal-scrim--palette" onClick={this.closeAll}>
            <div className="cmc-manual-modal cmc-manual-modal--palette" onClick={this.stop}>
              <div className="cmc-manual-palette-head">
                <span style={{ color: 'var(--cmc-muted-4)', fontSize: 14 }}>⌕</span>
                <input
                  ref={this.searchRef}
                  className="cmc-manual-palette-input"
                  value={s.query}
                  placeholder="Buscar módulos, tópicos e documentações…"
                  onChange={(e) => this.setState({ query: e.target.value })}
                />
                <span className="cmc-manual-kbd">ESC</span>
              </div>
              <div className="cmc-manual-palette-body">
                {v.results.map((r, idx) => (
                  <div key={idx} className="cmc-manual-palette-row" onClick={r.onClick}>
                    <span className="cmc-manual-palette-kind">{r.kind}</span>
                    <span className="cmc-manual-palette-label">{r.label}</span>
                    <span className="cmc-manual-palette-path">{r.path}</span>
                  </div>
                ))}
                {v.noResults && (
                  <div className="cmc-manual-palette-empty">Nenhum resultado para “{s.query}”.</div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )
      );
    }

    return nodes;
  }

  render() {
    const v = this.renderVals();
    const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

    if (this.state.loading && !this.state.modules.length) {
      return (
        <div className="cmc-manual" role="region" aria-label="Manual do cliente">
          <ProcessConfigSkeleton variant="listBody" ariaLabel="Carregando manual" />
        </div>
      );
    }

    if (this.state.loadError && !this.state.modules.length) {
      return (
        <div className="cmc-manual" role="region" aria-label="Manual do cliente">
          <div style={{ margin: 'auto', textAlign: 'center', padding: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Não foi possível carregar</div>
            <div style={{ fontSize: 12.5, color: 'var(--cmc-muted-2)', marginBottom: 14 }}>{this.state.loadError}</div>
            <button type="button" className="cmc-manual-accent-btn" onClick={() => this.reloadHierarchy()}>
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="cmc-manual" role="region" aria-label="Manual do cliente">
        <aside className="cmc-manual-sidebar">
          <div className="cmc-manual-brand">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="cmc-manual-brand-title">Documentação</div>
              <div className="cmc-manual-brand-sub">
                {v.totalDocs} páginas · {v.totalModules} módulos
              </div>
            </div>
            <Tip label="Backup" placement="bottom">
              <button
                type="button"
                className="cmc-manual-backup-btn"
                aria-label="Backup da documentação"
                onClick={this.openBackupModal}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 3v10m0 0l3.5-3.5M12 13L8.5 9.5M5 17.5V19a2 2 0 002 2h10a2 2 0 002-2v-1.5"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </Tip>
          </div>

          <div className="cmc-manual-side-actions">
            <button
              type="button"
              className="cmc-manual-search-btn"
              onClick={() => this.setState({ palette: true, query: '', imgBox: null, fmt: null, slash: null })}
            >
              <span style={{ fontSize: 13 }}>⌕</span>
              <span style={{ flex: 1 }}>Buscar em tudo</span>
              <span className="cmc-manual-kbd">{isMac ? '⌘K' : 'Ctrl K'}</span>
            </button>
            <button type="button" className="cmc-manual-primary-btn" onClick={this.newModule}>
              <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> Novo módulo
            </button>
          </div>

          <div className="cmc-manual-hier-head">
            <span className="cmc-manual-hier-label">Hierarquia</span>
            <button type="button" className="cmc-manual-linkish" onClick={() => this.changeSel({ view: 'overview' })}>
              visão geral
            </button>
          </div>

          <div className="cmc-manual-tree">
            {v.tree.map((m) => (
              <div
                key={m.id}
                className={'cmc-manual-tree-mod' + (m.dropClass || '')}
                style={{ marginBottom: 1 }}
                draggable
                onDragStart={m.onDragStart}
                onDragOver={m.onDragOver}
                onDragLeave={m.onDragLeave}
                onDrop={m.onDrop}
                onDragEnd={m.onDragEnd}
              >
                <div className="cmc-manual-tree-row" style={{ background: m.bg }} onClick={m.onToggle} onContextMenu={m.onMenu}>
                  <span className="cmc-manual-chev" style={{ transform: 'rotate(' + m.rot + ')' }}>
                    ▶
                  </span>
                  <span className="cmc-manual-tree-name">{m.name}</span>
                  <span className="cmc-manual-tree-count">{m.count}</span>
                  <Tip label="Novo tópico">
                    <button type="button" className="cmc-manual-icon-btn" onClick={m.onAdd}>
                      +
                    </button>
                  </Tip>
                  <Tip label="Opções">
                    <button type="button" className="cmc-manual-icon-btn cmc-manual-icon-btn--dots" onClick={m.onMenu}>
                      ⋯
                    </button>
                  </Tip>
                </div>
                {m.open && (
                  <div className="cmc-manual-tree-children">
                    {m.topics.map((t) => (
                      <div
                        key={t.id}
                        className={'cmc-manual-tree-topic-wrap' + (t.dropClass || '')}
                        draggable
                        onDragStart={t.onDragStart}
                        onDragOver={t.onDragOver}
                        onDrop={t.onDrop}
                        onDragEnd={t.onDragEnd}
                      >
                        <div
                          className="cmc-manual-tree-topic"
                          style={{ background: t.bg }}
                          onClick={t.onSelect}
                          onContextMenu={t.onMenu}
                        >
                          <span
                            className="cmc-manual-chev cmc-manual-chev--sm"
                            style={{ transform: 'rotate(' + t.rot + ')' }}
                            onClick={t.onToggle}
                          >
                            ▶
                          </span>
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: 12.5,
                              color: t.fg,
                              fontWeight: t.fw,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {t.name}
                          </span>
                          <Tip label="Nova documentação">
                            <button type="button" className="cmc-manual-icon-btn" onClick={t.onAdd}>
                              +
                            </button>
                          </Tip>
                          <Tip label="Opções">
                            <button type="button" className="cmc-manual-icon-btn cmc-manual-icon-btn--dots" onClick={t.onMenu}>
                              ⋯
                            </button>
                          </Tip>
                        </div>
                        {t.open && (
                          <div className="cmc-manual-tree-docs">
                            {t.docs.map((d) => (
                              <div
                                key={d.id}
                                className={'cmc-manual-tree-doc' + (d.dropClass || '')}
                                style={{ background: d.bg }}
                                draggable
                                onDragStart={d.onDragStart}
                                onDragOver={d.onDragOver}
                                onDrop={d.onDrop}
                                onDragEnd={d.onDragEnd}
                                onClick={d.onSelect}
                                onContextMenu={d.onMenu}
                              >
                                <span style={{ width: 10, flex: 'none', fontSize: 10, color: d.dot }}>•</span>
                                <span
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: 12.5,
                                    color: d.fg,
                                    fontWeight: d.fw,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {d.title}
                                </span>
                                <Tip label="Opções">
                                  <button type="button" className="cmc-manual-icon-btn cmc-manual-icon-btn--dots" onClick={d.onMenu}>
                                    ⋯
                                  </button>
                                </Tip>
                              </div>
                            ))}
                            {t.empty && (
                              <button type="button" className="cmc-manual-empty-link" onClick={t.onAdd}>
                                + primeira documentação
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {m.empty && (
                      <button type="button" className="cmc-manual-empty-link" onClick={m.onAdd}>
                        + primeiro tópico
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {v.treeEmpty && (
              <div className="cmc-manual-tree-empty">
                <div className="cmc-manual-tree-empty-title">Nada por aqui</div>
                <div className="cmc-manual-tree-empty-sub">Comece criando o primeiro módulo do manual.</div>
                <button type="button" className="cmc-manual-accent-btn" onClick={this.newModule}>
                  Criar módulo
                </button>
              </div>
            )}
          </div>

          <div className="cmc-manual-side-foot">
            <div className="cmc-manual-side-foot-hint cmc-manual-grad-text">Arraste para mover itens</div>
          </div>
        </aside>

        <main className="cmc-manual-main">
          <header className="cmc-manual-topbar">
            <div className="cmc-manual-crumbs">
              {v.crumbs.map((c, i) => (
                <span
                  key={i}
                  className="cmc-manual-crumb"
                  style={{ color: c.fg, fontWeight: c.fw }}
                  onClick={c.onClick}
                >
                  <span className="cmc-manual-crumb-label">{c.label}</span>
                  <span className="cmc-manual-crumb-sep">{c.sep}</span>
                </span>
              ))}
            </div>
            {v.isDoc && (
              <div className="cmc-manual-top-actions">
                <span className="cmc-manual-saved">{v.savedLabel}</span>
                <button
                  type="button"
                  className="cmc-manual-ghost-btn"
                  onClick={() =>
                    this.setState({
                      move: { kind: 'd', ids: this.state.sel },
                      imgBox: null,
                      fmt: null,
                      slash: null,
                    })
                  }
                >
                  Mover para…
                </button>
                <button type="button" className="cmc-manual-ghost-btn" onClick={this.insertImage}>
                  Inserir imagem
                </button>
              </div>
            )}
          </header>

          <div className="cmc-manual-scroll">
            {v.isOverview && (
              <div className="cmc-manual-overview">
                <div className="cmc-manual-ov-head">
                  <div>
                    <h1 className="cmc-manual-ov-title">Manual do sistema</h1>
                    <p className="cmc-manual-ov-sub cmc-manual-grad-text">
                      Módulo › Tópico › Documentação. Tudo o que já foi documentado, em um só lugar.
                    </p>
                  </div>
                  <button type="button" className="cmc-manual-primary-btn cmc-manual-primary-btn--inline" onClick={this.newModule}>
                    + Novo módulo
                  </button>
                </div>

                {v.showMetrics && (
                  <div className="cmc-manual-metrics">
                    {v.metrics.map((k) => (
                      <div key={k.label} className="cmc-manual-metric">
                        <div className="cmc-manual-metric-label">{k.label}</div>
                        <div className="cmc-manual-metric-value">{k.value}</div>
                        <div className="cmc-manual-metric-hint">{k.hint}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="cmc-manual-cards">
                  {v.cards.map((c) => (
                    <div
                      key={c.name}
                      className="cmc-manual-card"
                      onDragOver={c.onDragOver}
                      onDrop={c.onDrop}
                    >
                      <div className="cmc-manual-card-head">
                        <div className="cmc-manual-card-initial">{c.initial}</div>
                        <div className="cmc-manual-card-name" onClick={c.onOpen}>
                          {c.name}
                        </div>
                        <button type="button" className="cmc-manual-text-btn" onClick={c.onRename}>
                          editar
                        </button>
                        <button type="button" className="cmc-manual-text-btn cmc-manual-text-btn--danger" onClick={c.onDelete}>
                          excluir
                        </button>
                      </div>
                      <div className="cmc-manual-chips">
                        {c.topics.map((t) => (
                          <button key={t.name} type="button" className="cmc-manual-chip" onClick={t.onOpen}>
                            {t.name} <span className="cmc-manual-chip-count">{t.count}</span>
                          </button>
                        ))}
                        {c.empty && <span style={{ fontSize: 11.5, color: 'var(--cmc-muted-4)' }}>Sem tópicos ainda</span>}
                      </div>
                      <div className="cmc-manual-card-foot">
                        <div className="cmc-manual-progress">
                          <span style={{ width: c.pct }} />
                        </div>
                        <span className="cmc-manual-card-meta">{c.meta}</span>
                        <button type="button" className="cmc-manual-add-topic" onClick={c.onAddTopic}>
                          + tópico
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {v.isTopic && (
              <div className="cmc-manual-topic">
                <div className="cmc-manual-topic-head">
                  <div>
                    <div className="cmc-manual-topic-mod">{v.topicModuleName}</div>
                    <h1 className="cmc-manual-topic-title">{v.topicName}</h1>
                  </div>
                  <div className="cmc-manual-topic-actions">
                    <button
                      type="button"
                      className="cmc-manual-ghost-btn cmc-manual-ghost-btn--md"
                      onClick={() =>
                        this.setState({
                          move: { kind: 't', ids: this.state.sel },
                          imgBox: null,
                          fmt: null,
                          slash: null,
                        })
                      }
                    >
                      Mover tópico
                    </button>
                    <button type="button" className="cmc-manual-primary-btn cmc-manual-primary-btn--inline" onClick={v.addDocHere}>
                      + Documentação
                    </button>
                  </div>
                </div>
                <div className="cmc-manual-doc-grid">
                  {v.topicDocs.map((d) => (
                    <div key={d.title} className="cmc-manual-doc-card" onClick={d.onOpen}>
                      <div className="cmc-manual-doc-card-title">{d.title}</div>
                      <div className="cmc-manual-doc-card-excerpt">{d.excerpt}</div>
                      <div className="cmc-manual-doc-card-when">{d.when}</div>
                    </div>
                  ))}
                </div>
                {v.topicEmpty && (
                  <div className="cmc-manual-topic-empty">
                    <div className="cmc-manual-topic-empty-title">Tópico sem documentação</div>
                    <div className="cmc-manual-topic-empty-sub">
                      Crie a primeira página deste tópico para começar o manual.
                    </div>
                    <button type="button" className="cmc-manual-accent-btn cmc-manual-accent-btn--lg" onClick={v.addDocHere}>
                      + Nova documentação
                    </button>
                  </div>
                )}
              </div>
            )}

            {v.isDoc && (
              <div className="cmc-manual-editor-wrap">
                <div className="cmc-manual-editor-path">{v.docPath}</div>
                <input
                  className="cmc-manual-editor-title"
                  value={v.docTitle}
                  onChange={v.onTitle}
                  placeholder="Título da documentação"
                />
                <div className="cmc-manual-editor-hints cmc-manual-grad-text">
                  Digite / para blocos · Selecione o texto para formatar · Clique na imagem e arraste as bordas ·
                  Ctrl+V cola imagem ou texto limpo
                </div>
                <div
                  ref={this.editorRef}
                  className="cmc-manual-editor"
                  contentEditable
                  suppressContentEditableWarning
                  data-ph="Escreva a documentação…"
                  onInput={this.onEditorInput}
                  onPaste={this.onEditorPaste}
                  onKeyUp={this.onEditorKey}
                  onPointerDown={this.onEditorPointerDown}
                  onPointerUp={this.onEditorPointerUp}
                  onClick={this.onEditorClick}
                />
              </div>
            )}
          </div>
        </main>

        {this.renderOverlays(v)}
      </div>
    );
  }
}
